import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, X, Music } from "lucide-react";
import { useRadioPlayer } from "../context/RadioPlayerContext";

/**
 * RadioPlayer — sticky bottom playback dock. Plays YouTube embeds in an
 * invisible iframe (muted-fallback fine for now), Spotify embeds via their
 * official widget, and direct URLs via <audio>. Always shows the album
 * cover + title + artist + click-through to the station.
 */
export default function RadioPlayer() {
  const { current, playing, next, prev, toggle, clear, volume, setVolume } = useRadioPlayer();
  const [muted, setMuted] = useState(false);
  const audioRef = useRef(null);
  const iframeRef = useRef(null);

  // Apply volume / mute to <audio> element
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = muted ? 0 : volume;
    if (playing) a.play().catch(() => {});
    else a.pause();
  }, [playing, volume, muted, current?.id]);

  // When track changes, re-attach to <audio> only if it's an url|upload
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.onended = next;
  }, [next]);

  if (!current) return null;

  const isYoutube = current.source_type === "youtube" || /youtube\.com|youtu\.be/.test(current.source_url || "");
  const isSpotify = current.source_type === "spotify" || /open\.spotify\.com/.test(current.source_url || "");
  const isEmbed = isYoutube || isSpotify;

  return (
    <div
      data-testid="radio-player"
      className="fixed bottom-0 inset-x-0 z-50 bg-gradient-to-t from-black via-black/95 to-black/80 border-t border-tv-accent/40 text-white"
      style={{ backdropFilter: "blur(14px)" }}
    >
      <div className="max-w-[1600px] mx-auto flex items-center gap-3 px-3 sm:px-6 py-2.5">
        {/* Artwork + track meta */}
        <Link
          to={current.station_slug ? `/radio/station/${current.station_slug}` : "/radio"}
          data-testid="radio-player-cover"
          className="shrink-0 flex items-center gap-3 min-w-0 max-w-[52%]"
        >
          <div
            className="w-12 h-12 rounded bg-white/10 overflow-hidden shrink-0"
            style={{
              backgroundImage: `url(${current.cover_url || ""})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          <div className="min-w-0">
            <div data-testid="radio-player-title" className="font-bold text-sm line-clamp-1">{current.title}</div>
            <div data-testid="radio-player-artist" className="text-white/60 text-xs line-clamp-1">{current.artist}</div>
          </div>
        </Link>

        {/* Transport */}
        <div className="flex items-center gap-1 ml-auto shrink-0">
          <button data-testid="radio-player-prev" onClick={prev} title="Previous" className="p-2 text-white/70 hover:text-white">
            <SkipBack className="w-4 h-4" />
          </button>
          <button
            data-testid="radio-player-toggle"
            onClick={toggle}
            title={playing ? "Pause" : "Play"}
            className="bg-tv-accent hover:bg-yellow-400 text-black p-2.5 rounded-full"
          >
            {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          <button data-testid="radio-player-next" onClick={next} title="Next" className="p-2 text-white/70 hover:text-white">
            <SkipForward className="w-4 h-4" />
          </button>
        </div>

        {/* Volume */}
        <div className="hidden md:flex items-center gap-2 ml-2 shrink-0">
          <button
            data-testid="radio-player-mute"
            onClick={() => setMuted((m) => !m)}
            className="text-white/70 hover:text-white"
            title={muted ? "Unmute" : "Mute"}
          >
            {muted || volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
          <input
            data-testid="radio-player-volume"
            type="range"
            min="0"
            max="1"
            step="0.02"
            value={muted ? 0 : volume}
            onChange={(e) => { setMuted(false); setVolume(parseFloat(e.target.value)); }}
            className="w-24 accent-tv-accent"
            aria-label="Volume"
          />
        </div>

        <button
          data-testid="radio-player-close"
          onClick={clear}
          title="Close player"
          className="shrink-0 p-2 text-white/40 hover:text-white"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Hidden media elements — one or the other depending on source.
          YouTube embeds use full autoplay + controls so "Full Song" entries
          play the real track (not just the 30s preview). */}
      {isEmbed ? (
        <iframe
          ref={iframeRef}
          key={current.id + (playing ? "-p" : "-s")}
          title={current.title}
          src={buildEmbedSrc(current, playing)}
          allow="autoplay; encrypted-media; fullscreen"
          allowFullScreen
          className="fixed bottom-[68px] right-4 rounded-lg shadow-2xl z-40 border-2 border-tv-accent/60"
          style={{
            width: isYoutube ? 280 : 300,
            height: isYoutube ? 158 : 80,
            background: "#000",
            display: playing ? "block" : "none",
          }}
        />
      ) : (
        <audio
          ref={audioRef}
          key={current.id}
          src={current.source_url}
          preload="auto"
        />
      )}
    </div>
  );
}

function buildEmbedSrc(track, playing) {
  const url = track.source_url || "";
  const ytId = ytIdFromUrl(url);
  if (ytId) {
    // Full-size YouTube embed so full-length tracks actually play.
    // `controls=1` lets the user scrub; `enablejsapi=1` so we can pause
    // from the dock in a later pass.
    return `https://www.youtube.com/embed/${ytId}?autoplay=${playing ? 1 : 0}&controls=1&modestbranding=1&playsinline=1&rel=0&enablejsapi=1`;
  }
  // Handle the YouTube SEARCH URL we store for "Full Song" picks — grab
  // the first result by embedding the search channel which auto-plays.
  if (/youtube\.com\/results/.test(url)) {
    try {
      const u = new URL(url);
      const q = u.searchParams.get("search_query") || "";
      if (q) return `https://www.youtube-nocookie.com/embed?listType=search&list=${encodeURIComponent(q)}&autoplay=${playing ? 1 : 0}`;
    } catch {}
  }
  const sp = spotifyEmbedFromUrl(url);
  if (sp) return sp;
  return url;
}

function ytIdFromUrl(url) {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtube.com")) return u.searchParams.get("v");
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1);
  } catch {}
  return null;
}

function spotifyEmbedFromUrl(url) {
  try {
    const u = new URL(url);
    if (!u.hostname.includes("spotify.com")) return null;
    // e.g. https://open.spotify.com/track/abc → https://open.spotify.com/embed/track/abc
    return `https://open.spotify.com${u.pathname.startsWith("/embed") ? "" : "/embed"}${u.pathname}`;
  } catch {}
  return null;
}

/** Small icon used in contexts where a visual "Radio" signal is handy. */
export function NowPlayingBadge() {
  const { current, playing } = useRadioPlayer();
  if (!current) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-tv-accent">
      <Music className={`w-3 h-3 ${playing ? "animate-pulse" : ""}`} /> {current.title}
    </span>
  );
}
